import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { apiBase, createClient, isApiError, type ApiClient } from "../api/client";
import { createSessionStore, localTokenStore, type SessionStore, type TokenStore } from "../api/session";

export interface Services {
  client: ApiClient;
  session: SessionStore;
  queryClient: QueryClient;
  /** Current instant. Injected so tests can pin "now". */
  now: () => number;
}

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // 4xx will not fix itself; one retry for network blips and 5xx.
        retry: (count, err) => (isApiError(err) && err.status >= 400 && err.status < 500 ? false : count < 1),
      },
    },
  });
}

export interface ServiceOptions {
  tokenStore?: TokenStore;
  baseUrl?: () => string;
  now?: () => number;
  currentPath?: () => string;
  queryClient?: QueryClient;
}

export function createServices(opts: ServiceOptions = {}): Services {
  const session = createSessionStore(opts.tokenStore ?? localTokenStore());
  const queryClient = opts.queryClient ?? createQueryClient();
  const client = createClient({
    baseUrl: opts.baseUrl ?? apiBase,
    getToken: () => session.getState().token,
    onUnauthenticated: () => {
      const path = opts.currentPath ? opts.currentPath() : `${window.location.pathname}${window.location.search}`;
      session.signOut(path.startsWith("/signin") ? null : path);
      queryClient.clear();
    },
  });
  return { client, session, queryClient, now: opts.now ?? Date.now };
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return (
    <ServicesContext.Provider value={services}>
      <QueryClientProvider client={services.queryClient}>{children}</QueryClientProvider>
    </ServicesContext.Provider>
  );
}

export function useServices(): Services {
  const s = useContext(ServicesContext);
  if (!s) throw new Error("useServices outside ServicesProvider");
  return s;
}

export const useMemoServices = (make: () => Services) => useMemo(make, []); // eslint-disable-line react-hooks/exhaustive-deps
